"use client";
import React, {createContext, useState, useEffect, useCallback} from "react";
import { BASE_URL, CDN_URL } from "../config/apiconfig";

export const ProductContext = createContext(null);

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  const [productsLoading, setProductsLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState(null);

  const [categoryProducts, setCategoryProducts] = useState([]);
  const [categoryProductsLoading, setCategoryProductsLoading] = useState(false);

  /* ---------------- HELPER FUNCTION TO PARSE DESCRIPTION ---------------- */
  const parseDescription = (descriptionString) => {
    if (!descriptionString) return [];

    try {
      const parsed = JSON.parse(descriptionString);
      if (Array.isArray(parsed)) {
        return parsed.map(item => ({
          title: item.title || '',
          content: item.content || '',
          image: item.image ? `${CDN_URL}/${item.image}` : null
        }));
      }
      return [];
    } catch (error) {
      console.warn("Failed to parse description JSON:", error);
      return [];
    }
  };

  /* ---------------- SAFE NUMBER PARSING HELPER ---------------- */
  const safeParseNumber = (value, defaultValue = 0) => {
    if (value === null || value === undefined || value === '') {
      return defaultValue;
    }

    const parsed = Number(value);
    return isNaN(parsed) ? defaultValue : parsed;
  };

  /* ---------------- FETCH PRODUCT REVIEWS ---------------- */
  const fetchProductReviews = useCallback(async (productId) => {
    if (!productId) {
      console.warn("fetchProductReviews: No productId provided");
      return;
    }

    console.log("Fetching reviews for product:", productId);
    setReviewsLoading(true);

    try {
      const res = await fetch(`${BASE_URL}/reviews/${productId}`);

      if (!res.ok) {
        console.error("Reviews API response not ok:", res.status, res.statusText);
        throw new Error(`Failed to fetch reviews: ${res.status}`);
      }

      const data = await res.json();
      console.log("Reviews API response:", data);

      const reviewData = data?.data || {};

      // Extract and validate rating data with safe parsing
      const reviews = reviewData.reviews || [];
      const avgRating = safeParseNumber(reviewData.average_rating, 0);
      const totalCount = safeParseNumber(reviewData.total_reviews, 0);

      console.log("Processed rating data:", {
        reviews: reviews.length,
        avgRating,
        totalCount
      });

      setReviews(reviews);
      setAverageRating(avgRating);
      setTotalReviews(totalCount);

      // If no reviews from API, try to calculate from reviews array
      if (totalCount === 0 && reviews.length > 0) {
        const calculatedAvg = reviews.reduce((sum, review) => {
          return sum + safeParseNumber(review.rating, 0);
        }, 0) / reviews.length;
        console.log("Calculated average from reviews array:", calculatedAvg);
        setAverageRating(calculatedAvg);
        setTotalReviews(reviews.length);
      }

    } catch (err) {
      console.error("Reviews fetch error:", err);
      setReviews([]);
      setAverageRating(0);
      setTotalReviews(0);
    } finally {
      setReviewsLoading(false);
    }
  }, []);

  /* ---------------- FETCH ALL PRODUCTS ---------------- */
  const fetchProducts = useCallback(async () => {
    console.log("Fetching products from API (no cache)");
    setProductsLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/products`);
      const data = await res.json();

      const list = data?.data || data?.products || [];

      if (!Array.isArray(list)) return;

      const mapped = list.map((p) => {
        let images = [];
        try {
          images = JSON.parse(p.product_images || "[]");
        } catch {}

        const fullImages = images.map(
          (img) => `${CDN_URL}/${img}`
        );

        // Handle video URL
        const videoUrl = p.video ? `${CDN_URL}/${p.video}` : null;

        // Format dimensions
        const formatDimensions = () => {
          const length = p.item_length_longer_edge;
          const lengthUnit = p.item_length_unit;
          const width = p.item_width_shorter_edge;
          const widthUnit = p.item_width_unit;

          if (length && width && lengthUnit && widthUnit) {
            return `${length}${lengthUnit} x ${width}${widthUnit}`;
          }
          return null;
        };

        return {
          id: String(p.id),
          name: p.product_name ?? "",
          description: p.description ?? "",
          descriptionData: parseDescription(p.description), // Parse description JSON
          // Fix price mappings: max_retail_price is original price, your_price is offer price
          price: safeParseNumber(p.max_retail_price, 0), // Original price
          offerPrice: safeParseNumber(p.your_price, 0), // Offer/Sale price
          categoryId: String(p.category_id),
          subcategoryId: String(p.subcategory_id),
          videoUrl: videoUrl,
          categoryName: p.category_name ?? "",
          subcategoryName: p.subcategory_name ?? "",
          image: fullImages[0] || "/placeholder.png",
          images: fullImages,
          video: videoUrl, // Add video field
          // Include any existing rating data from product with safe parsing
          rating: safeParseNumber(p.rating, 0),
          ratingCount: safeParseNumber(p.rating_count || p.ratingCount, 0),
          // Add minimum order quantity field
          min_order_qty: safeParseNumber(p.min_order_qty, 1),
          minimumOrderQty: safeParseNumber(p.min_order_qty, 1), // Alternative field name for compatibility
          // New fields for specifications
          color: p.color || null,
          colorCode: p.color_code || null, // Add dynamic color code from API
          material: p.material || null,
          dimensions: formatDimensions(),
          size: p.size || null,
          // Add return policy field
          product_policy: p.product_policy || null,
        };
      });

      setProducts(mapped);
      console.log(`Successfully fetched ${mapped.length} products`);
    } catch (err) {
      console.error("Products fetch error:", err);
    } finally {
      setProductsLoading(false);
    }
  }, []);

  /* ---------------- FETCH PRODUCT BY ID ---------------- */
  const fetchProductById = useCallback(async (id) => {
    if (!id) {
      console.warn("fetchProductById: No id provided");
      setProductError("No product ID provided");
      setProduct(null);
      return;
    }

    console.log("Fetching product by ID:", id);
    setProductLoading(true);
    setProductError(null);

    // Reset product and rating data when fetching new product
    setProduct(null);
    setAverageRating(0);
    setTotalReviews(0);
    setReviews([]);

    try {
      const res = await fetch(`${BASE_URL}/products/${id}`);
      console.log("Product API raw response:", res);
      // Handle different response statuses
      if (!res.ok) {
        if (res.status === 404) {
          setProductError("Product not found");
          setProduct(null);
          console.error("Product not found - 404");
          return;
        } else if (res.status === 204) {
          setProductError("Product not found");
          setProduct(null);
          console.error("Product not found - 204");
          return;
        } else {
          setProductError(`Failed to fetch product: ${res.status} ${res.statusText}`);
          setProduct(null);
          console.error("Product API error:", res.status, res.statusText);
          return;
        }
      }

      const data = await res.json();
      console.log("Product API response:", data);

      // Check for API response indicating product not found
      if (data?.status === "204" && data?.message === "Product not found") {
        setProductError("Product not found");
        setProduct(null);
        console.error("Product not found - API returned 204 status");
        return;
      }

      const p =
        data?.data && !Array.isArray(data.data)
          ? data.data
          : Array.isArray(data?.data)
          ? data.data[0]
          : data?.product;

      if (!p) {
        setProductError("Product not found");
        setProduct(null);
        console.error("Product data is null or undefined");
        return;
      }

      console.log("Product data found:", p);

      let images = [];
      try {
        images = JSON.parse(p.product_images || "[]");
      } catch {}

      const fullImages = images.map(
        (img) => `${CDN_URL}/${img}`
      );

      // Handle video URL
      const videoUrl = p.video ? `${CDN_URL}/${p.video}` : null;

      // Format dimensions
      const formatDimensions = () => {
        const length = p.item_length_longer_edge;
        const lengthUnit = p.item_length_unit;
        const width = p.item_width_shorter_edge;
        const widthUnit = p.item_width_unit;

        if (length && width && lengthUnit && widthUnit) {
          return `${length}${lengthUnit} x ${width}${widthUnit}`;
        }
        return null;
      };

      const productData = {
        id: String(p.id),
        name: p.product_name ?? "",
        description: p.description ?? "",
        descriptionData: parseDescription(p.description), // Parse description JSON
        // Fix price mappings: max_retail_price is original price, your_price is offer price
        price: safeParseNumber(p.max_retail_price, 0), // Original price
        offerPrice: safeParseNumber(p.your_price, 0), // Offer/Sale price
        categoryId: String(p.category_name),
        image: fullImages[0] || "/placeholder.png",
        images: fullImages,
        video: videoUrl, // Add video field
        videoUrl: videoUrl, // Keep both for compatibility
        // Include any existing rating data from product with safe parsing
        rating: safeParseNumber(p.rating, 0),
        ratingCount: safeParseNumber(p.rating_count || p.ratingCount, 0),
        // Include average_rating from API with safe parsing
        averageRating: safeParseNumber(p.average_rating, 0),
        // Add minimum order quantity field
        min_order_qty: safeParseNumber(p.min_order_qty, 1),
        minimumOrderQty: safeParseNumber(p.min_order_qty, 1), // Alternative field name for compatibility
        // New fields for specifications
        color: p.color || null,
        colorCode: p.color_code || null, // Add dynamic color code from API
        material: p.material || null,
        dimensions: formatDimensions(),
        size: p.size || null,
        // Add return policy field
        product_policy: p.product_policy || null,
      };

      console.log("Setting product data:", productData);
      console.log("Product average_rating from API:", p.average_rating, "-> parsed as:", productData.averageRating);

      setProduct(productData);
      setProductError(null);

      // Fetch reviews and ratings for this product
      await fetchProductReviews(p.id);

    } catch (err) {
      console.error("Product fetch error:", err);
      setProductError("Failed to load product. Please try again.");
      setProduct(null);
    } finally {
      setProductLoading(false);
    }
  }, [fetchProductReviews]);

  /* ---------------- FETCH PRODUCTS BY CATEGORY ---------------- */
  const fetchProductsByCategory = useCallback(async (categoryId) => {
    if (!categoryId) {
      console.warn("fetchProductsByCategory: No categoryId provided");
      return [];
    }

    console.log(`Fetching products for category ${categoryId} from API (no cache)`);

    try {
      const res = await fetch(`${BASE_URL}/products/category/${categoryId}`);

      if (!res.ok) {
        throw new Error(`Failed to fetch category products: ${res.status}`);
      }

      const data = await res.json();
      const list = data?.data || [];

      if (!Array.isArray(list)) {
        console.warn(`Invalid data format for category ${categoryId}`);
        return [];
      }

      const mapped = list.map((p) => {
        let images = [];
        try {
          images = JSON.parse(p.product_images || "[]");
        } catch {}

        const fullImages = images.map(
          (img) => `${CDN_URL}/${img}`
        );

        // Handle video URL
        const videoUrl = p.video ? `${CDN_URL}/${p.video}` : null;

        // Format dimensions
        const formatDimensions = () => {
          const length = p.item_length_longer_edge;
          const lengthUnit = p.item_length_unit;
          const width = p.item_width_shorter_edge;
          const widthUnit = p.item_width_unit;

          if (length && width && lengthUnit && widthUnit) {
            return `${length}${lengthUnit} x ${width}${widthUnit}`;
          }
          return null;
        };

        return {
          id: String(p.id),
          name: p.product_name ?? "",
          description: p.description ?? "",
          descriptionData: parseDescription(p.description), // Parse description JSON
          // Fix price mappings: max_retail_price is original price, your_price is offer price
          price: safeParseNumber(p.max_retail_price, 0), // Original price
          offerPrice: safeParseNumber(p.your_price, 0), // Offer/Sale price
          categoryId: String(p.category_id),
          subcategoryId: String(p.subcategory_id),
          videoUrl: videoUrl,
          categoryName: p.category_name ?? "",
          subcategoryName: p.subcategory_name ?? "",
          image: fullImages[0] || "/placeholder.png",
          images: fullImages,
          video: videoUrl, // Add video field
          // Include any existing rating data from product with safe parsing
          rating: safeParseNumber(p.rating, 0),
          ratingCount: safeParseNumber(p.rating_count || p.ratingCount, 0),
          // Add minimum order quantity field
          min_order_qty: safeParseNumber(p.min_order_qty, 1),
          minimumOrderQty: safeParseNumber(p.min_order_qty, 1), // Alternative field name for compatibility
          // New fields for specifications
          color: p.color || null,
          colorCode: p.color_code || null, // Add dynamic color code from API
          material: p.material || null,
          dimensions: formatDimensions(),
          size: p.size || null,
          // Add return policy field
          product_policy: p.product_policy || null,
        };
      });

      console.log(`Successfully fetched ${mapped.length} products for category ${categoryId}`);
      return mapped;

    } catch (err) {
      console.error(`Category products fetch error for category ${categoryId}:`, err);
      return [];
    }
  }, []);

  /* ---------------- INIT ---------------- */
  useEffect(() => {
    console.log("ProductProvider mounted, fetching fresh products");
    fetchProducts();
  }, [fetchProducts]);

  return (
    <ProductContext.Provider
      value={{
        products,
        product,
        productsLoading,
        productLoading: productLoading,
        loading: productLoading, // Keep backward compatibility
        productError,
        error: productError, // Keep backward compatibility
        fetchProductById,
        fetchProducts, // Expose fetchProducts for manual refresh
        // ✅ category
        categoryProducts,
        categoryProductsLoading,
        fetchProductsByCategory,
        // ✅ reviews and ratings
        reviews,
        averageRating,
        totalReviews,
        reviewsLoading,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};
